import React, { useRef, useEffect } from 'react';
import { useChatbot } from '../../context/ChatbotContext';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';

const Chatbot: React.FC = () => {
  const { 
    messages, 
    isOpen, 
    isMinimized, 
    isLoading,
    toggleChat, 
    minimizeChat, 
    maximizeChat 
  } = useChatbot();
  
  // Ref for chat messages container to enable auto-scrolling
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Auto-scroll to bottom of messages when new messages are added
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  
  // If chat is not open, render only the toggle button
  if (!isOpen) {
    return (
      <button
        onClick={toggleChat}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-primary text-white shadow-lg hover:bg-primary-dark transition-colors duration-200 flex items-center justify-center z-50"
        aria-label="Open chat"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className="h-6 w-6" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" 
          />
        </svg>
      </button>
    );
  }
  
  // If chat is minimized, show just the header bar
  if (isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 w-80 rounded-t-lg bg-white shadow-lg border border-gray-200 overflow-hidden z-50 dark:bg-neutral-800 dark:border-neutral-700">
        <div className="flex items-center justify-between px-4 py-3 bg-primary text-white cursor-pointer" onClick={maximizeChat}>
          <div className="font-medium">CodemyAI Assistant</div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                maximizeChat();
              }}
              className="text-white hover:text-gray-200 transition-colors"
              aria-label="Maximize chat"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M5 15l7-7 7 7" 
                />
              </svg>
            </button>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                toggleChat();
              }}
              className="text-white hover:text-gray-200 transition-colors"
              aria-label="Close chat"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M6 18L18 6M6 6l12 12" 
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Full chat interface
  return (
    <div className="fixed bottom-6 right-6 w-80 md:w-96 h-[30rem] flex flex-col rounded-lg bg-white shadow-xl border border-gray-200 overflow-hidden z-50 dark:bg-neutral-800 dark:border-neutral-700">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-primary text-white">
        <div className="font-medium">CodemyAI Assistant</div>
        <div className="flex items-center space-x-2">
          <button 
            onClick={minimizeChat}
            className="text-white hover:text-gray-200 transition-colors"
            aria-label="Minimize chat"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M19 9l-7 7-7-7" 
              />
            </svg>
          </button>
          <button 
            onClick={toggleChat}
            className="text-white hover:text-gray-200 transition-colors"
            aria-label="Close chat"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-neutral-900">
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}
        
        {/* Show typing indicator when loading */}
        {isLoading && (
          <div className="flex items-center space-x-2 animate-pulse">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
          </div>
        )}
        
        {/* Hidden div for auto-scrolling */}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input */}
      <ChatInput isLoading={isLoading} />
    </div>
  );
};

export default Chatbot;
